import React, { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage, useT } from "@/lib/LanguageContext";
// Relative on purpose: real modules under Jest, where there is no "@" alias.
import FieldError from "../../components/ui/FieldError";
import { PATHWAYS_ADMIN as IDS } from "../../constants/testIds/pathwaysAdmin";
import { pathwayErrorMessage } from "../../pathways/errors";
import { fmtInstantDate } from "./format";

export const PROMPT_MAX = 5000;

/**
 * The weekly prompts of a cohort pathway (contract §5.1 `POST /prompts`), on its
 * dashboard: a composer, then the thread, newest first. The server numbers the week from
 * the start date and e-mails every learner who has not opted out of nudges; the learners
 * read the thread on their pathway home (spec screen 4).
 *
 * @param {object} props
 * @param {object} props.pathway    AdminPathway (its `thread`)
 * @param {(text: string) => Promise<void>} props.onPost   rejects with the raw error
 * @param {boolean} props.disabled  an archived pathway takes no new prompt
 */
export default function PromptsPanel({ pathway, onPost, disabled }) {
  const t = useT("admin");
  const tc = useT("common");
  const { lang } = useLanguage();
  const [text, setText] = useState("");
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState("");
  const thread = useMemo(
    () => [...(pathway.thread || [])].sort((a, b) => String(b.created_at).localeCompare(String(a.created_at))),
    [pathway.thread],
  );

  const post = async () => {
    const body = text.trim();
    if (!body) {
      setError(t("path.prompts.empty"));
      return;
    }
    setPosting(true);
    setError("");
    try {
      await onPost(body);
      setText("");
    } catch (e) {
      setError(pathwayErrorMessage(e, tc, tc("errors.saveFailed")));
    } finally {
      setPosting(false);
    }
  };

  return (
    <section data-testid={IDS.prompts} aria-labelledby="pathway-prompts-heading" className="bg-white rounded-3xl border border-eh-border-soft p-5 space-y-4">
      <div>
        <h2 id="pathway-prompts-heading" className="text-sm font-semibold text-eh-ink">{t("path.prompts.title")}</h2>
        <p className="text-sm text-eh-muted">{t("path.prompts.hint")}</p>
      </div>
      <div>
        <label htmlFor="pathway-prompt-text" className="sr-only">{t("path.prompts.label")}</label>
        <textarea
          id="pathway-prompt-text"
          value={text}
          onChange={(e) => { setText(e.target.value); setError(""); }}
          rows={3}
          maxLength={PROMPT_MAX}
          disabled={disabled || posting}
          placeholder={t("path.prompts.placeholder")}
          data-testid={IDS.promptText}
          className="w-full border border-input rounded-md px-3 py-2 text-sm bg-white resize-y"
        />
        <FieldError message={error} />
        <div className="mt-2 flex justify-end">
          <Button onClick={post} disabled={disabled || posting} data-testid={IDS.promptPost} className="bg-eh-ink text-eh-cream hover:bg-eh-ink/90">
            {posting && <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />}
            {t("path.prompts.post")}
          </Button>
        </div>
      </div>
      {thread.length === 0 ? (
        <p className="text-sm text-eh-muted">{t("path.prompts.none")}</p>
      ) : (
        <ol className="space-y-2">
          {thread.map((m) => (
            <li key={m.id} data-testid={IDS.prompt(m.id)} className="rounded-2xl bg-eh-cream/60 border border-eh-border-soft px-4 py-3">
              <p className="text-xs text-eh-muted">
                {t("path.prompts.week", { n: m.week })} · {fmtInstantDate(m.created_at, lang)}
              </p>
              <p className="text-sm text-eh-ink whitespace-pre-line mt-1">{m.text}</p>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}
